/**
 * Run reports — which formats a run has, and fetching one by name.
 *
 * A run's `reports/` folder holds the HTML report, and may also hold a PDF of
 * the same report beside it. Both single runs and batch runs use this.
 *
 * The folder download endpoint (`/folders/file/download?s3FolderPath=`) takes
 * a *folder* and resolves the one file inside it. With an HTML and a PDF in the
 * same folder it cannot be told which one to return, so once a PDF exists each
 * file is fetched by its exact name through the file-content endpoint instead.
 * A folder holding only the HTML keeps using the original folder download.
 *
 * A named file is read through a presigned GET for its exact key, not through
 * the file-content endpoint: that endpoint returns the file base64-encoded in a
 * Lambda response, which caps at 6 MB (~4.5 MB of file), and a consolidated
 * PDF with screenshots is easily larger. The content endpoint is kept only as
 * a fallback for when a presigned URL cannot be had.
 *
 * @module shared/middleware/services/ReportFileService
 */

import { FileService } from './FileService';
import { FILE_ENDPOINTS } from '../config/projectApiEndpoints';
import { noCacheAxios } from '../../config/cachedAxiosConfig';
import { getAuthHeaders } from '../../services/auth/authHeaders';
import { ProjectHeadersService } from './ProjectHeadersService';
import { toFileServicePath } from './S3JsonService';
import WorkerService from '../../workers/WorkerService';
import { createDownloadWorker } from '../../workers/downloadWorkerFactory';
import logger from '../../services/logging/logger';

export const REPORT_FORMATS = {
  html: { extension: 'html', mime: 'text/html', label: 'HTML' },
  pdf: { extension: 'pdf', mime: 'application/pdf', label: 'PDF' },
};

const IS_DEV = process.env.NODE_ENV === 'development';

/** Off-thread fetch of the report bytes; auto-terminates after 60s idle. */
const downloadWorker = new WorkerService(createDownloadWorker);

const extensionOf = (name) => String(name || '').split('.').pop().toLowerCase();

/**
 * Which report files a run has.
 *
 * @param {string} reportFolder  the run's `…/reports` folder, absolute or project-relative
 * @returns {Promise<{html: string|null, pdf: string|null}>} file names, null when absent.
 *          Never throws — a failed listing means "no PDF on offer", not an error.
 */
export const findReportFiles = async (projectId, reportFolder, userData) => {
  const folder = toFileServicePath(reportFolder, projectId);
  if (!projectId || !folder) return { html: null, pdf: null };

  const files = await FileService.listFilesByPrefix(projectId, folder.replace(/\/+$/, ''), userData);
  const pick = (ext) => (files.find((f) => extensionOf(f.name) === ext) || {}).name || null;
  return { html: pick('html'), pdf: pick('pdf') };
};

/** base64 → bytes, without assuming the file is text (a PDF is not). */
const base64ToBytes = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/** The small-file path: base64 inside a Lambda response, so capped at 6 MB. */
const fetchReportFileContent = async (projectId, folder, fileName, userData) => {
  const baseHeaders = await getAuthHeaders();
  const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
  const url = FILE_ENDPOINTS.getFileContent(projectId, fileName, folder);
  const response = await noCacheAxios.get(url, { headers });
  const content = response.data?.content;
  if (!content) throw new Error(`${fileName} has no content`);
  return base64ToBytes(content);
};

/**
 * Fetch one named file from a run's report folder.
 *
 * @returns {Promise<Uint8Array>} the file's raw bytes
 * @throws when the file is missing or carries no content
 */
export const fetchReportFile = async (projectId, reportFolder, fileName, userData) => {
  const folder = toFileServicePath(reportFolder, projectId).replace(/\/+$/, '');
  const s3Key = `projects/${projectId}/${folder}/${fileName}`;

  let presignedUrl = null;
  try {
    presignedUrl = (await FileService.getPresignedDownloadUrl(s3Key, userData))?.url || null;
  } catch (err) {
    logger.warn('Report presign failed, falling back to file content', { fileName, error: err.message });
  }

  if (presignedUrl) {
    const buffer = await downloadWorker.run(
      'DOWNLOAD_FILE', IS_DEV ? { url: presignedUrl, proxyUrl: '/s3-proxy' } : { url: presignedUrl }
    );
    logger.debug('Report file fetched by presigned URL', { fileName });
    return new Uint8Array(buffer);
  }

  const bytes = await fetchReportFileContent(projectId, folder, fileName, userData);
  logger.debug('Report file fetched by content endpoint', { fileName });
  return bytes;
};

/** Hand bytes or text to the browser as a download. */
export const saveReport = (data, fileName, mime) => {
  const blobUrl = URL.createObjectURL(new Blob([data], { type: mime }));
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
};

const ReportFileService = { findReportFiles, fetchReportFile, saveReport, REPORT_FORMATS };

export default ReportFileService;
